import React, { FC, memo } from 'react';

import { LocationStatePointsType, StreamGroupsCollectionPageParamsType } from 'application/pages/types';
import { RouterUtils, useLocation, useNavigate, useParams } from 'application/providers/RouterProvider';
import { SelectPlayerFlowType, useOnboardingActionContext, useOnboardingContext } from 'application/components/common';
import { TEXTS } from 'application/pages/texts';
import { STREAMS_CATEGORY_PATH, StreamsCategoryParamValues } from 'application/consts';
import { useNotificationContext } from 'application/providers/NotificationProvider';
import { useModalContext } from 'application/providers/ModalProvider';

import { Country } from 'domain/api/graphql/generated';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import Popper from '@zvuk-b2b/react-uikit/ui/Popper';

import useActionButtonsViewModel from './useActionButtonsViewModel';
import { ActionButtonsProps } from './types';
import { showCopyStreamError } from './utils';

import styles from './index.module.scss';

const ActionButtons: FC<ActionButtonsProps> = ({
  selectedPoints,
  isAcceptanceAdvancedPending,
  isLoading,
  isStreamBindButtonLoading,
  isStreamBindButtonDisabled,
  streamSetToPointsLoading,
  replaceModal,
  setIsStreamBindButtonLoading,
  handleChangeStream,
  copyTemplateStream,
  getSavedStreams,
  getStream,
  onboardingPassedRequest,
  refetchFeaturesOnboarding,
  readStreamWithThematic,
}) => {
  const { isTemplateMode, currentPoint, flowType } = useOnboardingContext();
  const { setInFlow, setCurrentPointStream, setCurrentPoint } = useOnboardingActionContext();
  const navigate = useNavigate();
  const location = useLocation<LocationStatePointsType>();
  const pathParams = useParams<StreamGroupsCollectionPageParamsType>();
  const notification = useNotificationContext();
  const { openModal } = useModalContext();

  const [pointFromLocation] = location.state?.pointsFromLocation || [];
  const targetStreamId = pathParams.id;
  const collectionId = pathParams.collectionId;

  const { handleBindStreamClick, handleAddPointClick, openAcceptanceAdvancedSigning } = useActionButtonsViewModel({
    targetStreamId,
    collectionId,
    isTemplateMode,
    selectedPoints,
    currentPoint,
    isFullOnboarding: flowType === SelectPlayerFlowType.FULL_ONBOARDING,
    setCurrentPoint,
    setCurrentPointStream,
    setInFlow,
    openModal,
    replaceModal,
    redirectToStreamCategoryPage: (streamId: string) => {
      navigate(
        RouterUtils.generatePath(STREAMS_CATEGORY_PATH, {
          category: StreamsCategoryParamValues.SAVED,
          collectionId: 0,
          id: streamId,
        })
      );
    },
    showCopyStreamWithThematicError: () => {
      notification.showErrorNotification({ children: TEXTS.CUSTOM_CONTENT_MODAL_STREAMS_NOTIFICATION_ERROR });
    },
    showCopyStreamWithThematicSuccess: () => {
      notification.showInfoNotification({ children: TEXTS.CUSTOM_CONTENT_MODAL_STREAMS_NOTIFICATION_SUCCESS });
    },
    showCopyStreamError: (country: Country) => {
      showCopyStreamError(notification, country);
    },
    setIsStreamBindButtonLoading,
    handleChangeStream,
    copyTemplateStream,
    getSavedStreams,
    getStream,
    onboardingPassedRequest,
    refetchFeaturesOnboarding,
    readStreamWithThematic,
  });

  return (
    <>
      <Button
        key="button_bind_stream_modal"
        elementId="button_bind_stream_modal"
        type={ButtonType.PRIMARY}
        isLoading={isLoading || isStreamBindButtonLoading || streamSetToPointsLoading}
        onClick={handleBindStreamClick}
        isDisabled={isStreamBindButtonDisabled}>
        {TEXTS.MODAL_BIND_BUTTON_SUBMIT_TEXT}
      </Button>
      {!pointFromLocation && (
        <Popper
          key="popper_create_new_point_modal"
          triggerNode={
            <div>
              <Button
                key="button_create_new_point_modal"
                elementId="button_create_new_point_modal"
                type={ButtonType.SECONDARY}
                isDisabled={isAcceptanceAdvancedPending}
                onClick={handleAddPointClick}>
                {TEXTS.MODAL_BIND_ADD_NEW_POINT_TEXT}
              </Button>
            </div>
          }
          isShow={isAcceptanceAdvancedPending}
          portalNode={document.body}
          containerClassName={styles.popperWrapper}
          popperClassName={styles.popper}>
          <span>{TEXTS.MODAL_BIND_ADD_NEW_POINT_HINT_ACCEPTANCE_ADVANCED}</span>
          <Button
            elementId="button_acceptance_advanced_open"
            className={styles.hintButton}
            type={ButtonType.PLAIN}
            onClick={openAcceptanceAdvancedSigning}>
            {TEXTS.MODAL_BIND_ADD_NEW_POINT_HINT_ACCEPTANCE_ADVANCED_BUTTON}
          </Button>
        </Popper>
      )}
    </>
  );
};

export default memo(ActionButtons);
